/**
 * Herramientas de productividad del gestor (2026-10-07): recordatorios, mensaje
 * a tarea del planner, resumen con IA y cómo se ven los tipos especiales
 * (encuestas, eventos, álbumes, menciones) y las reacciones.
 */
import "./gestor.css";
import "./bandeja.css";
import { rec_React as React, rec_api as api } from "../../runtime/index.jsx";
import { fechaHora, opcionesProgramar } from "./api-modos.js";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useMemo = (...a) => React.useMemo(...a);
const useState = (...a) => React.useState(...a);

/** Ventana base: Escape o tocar fuera la cierra. */
export function Modal({ titulo, onCerrar, children, ancho = 440 }) {
  return (
    <div className="gx gx-modal" onPointerDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
      onKeyDown={(e) => { if (e.key === `Escape`) { e.stopPropagation(); onCerrar(); } }}>
      <div className="gx-modal-caja" role="dialog" aria-modal="true" aria-label={titulo} style={{ width: `min(${ancho}px, 100%)` }}>
        <h2>{titulo}</h2>
        {children}
      </div>
    </div>
  );
}

/** Elegir una fecha: atajos o «otra». Devuelve Date o null. */
function Cuando({ valor, onCambiar, opciones }) {
  const [otra, setOtra] = useState(``);
  return (
    <div className="gx-opciones">
      {opciones.map((o) => (
        <label key={o.texto} className="gx-opcion">
          <input type="radio" name="cuando" checked={valor?.getTime() === o.cuando.getTime()} onChange={() => onCambiar(o.cuando)} />
          <span>{o.texto} <small style={{ display: `inline` }}>· {fechaHora(o.cuando)}</small></span>
        </label>
      ))}
      <label className="gx-opcion">
        <input type="radio" name="cuando" checked={Boolean(otra) && valor?.getTime() === new Date(otra).getTime()} onChange={() => otra && onCambiar(new Date(otra))} />
        <input type="datetime-local" className="gx-entrada" aria-label="Otra fecha y hora" value={otra}
          onChange={(e) => { setOtra(e.target.value); if (e.target.value) onCambiar(new Date(e.target.value)); }} />
      </label>
    </div>
  );
}

/** «Recuérdame responder» de un chat o de un mensaje. */
export function ModalRecordar({ api: gestor, chat, mensaje = null, onListo, onCerrar }) {
  const opciones = useMemo(() => {
    const ahora = new Date();
    const en3h = new Date(ahora.getTime() + 3 * 3_600_000);
    return [{ cuando: new Date(ahora.getTime() + 3_600_000), texto: `En 1 hora` }, { cuando: en3h, texto: `En 3 horas` },
      ...opcionesProgramar(ahora).filter((o) => o.texto !== `En 1 hora`)];
  }, []);
  const [para, setPara] = useState(opciones[0].cuando);
  const [nota, setNota] = useState(``);
  const [error, setError] = useState(``);
  const guardar = async (e) => {
    e.preventDefault();
    try {
      await gestor.crearRecordatorio({ chatId: chat.id, mensajeId: mensaje?.id ?? null, nota: nota.trim() || null, para });
      onListo();
    } catch (err) { setError(err.message); }
  };
  return (
    <Modal titulo={`Recordarme · ${chat.nombre}`} onCerrar={onCerrar}>
      <form className="gx-seccion" onSubmit={guardar}>
        {mensaje?.texto && <p className="gx-texto" style={{ margin: 0 }}>«{mensaje.texto.slice(0, 140)}{mensaje.texto.length > 140 ? `…` : ``}»</p>}
        <Cuando valor={para} onCambiar={setPara} opciones={opciones} />
        <label className="gx-campo">Nota (opcional)
          <input className="gx-entrada" maxLength={500} placeholder="Ej.: confirmar la hora" value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>
        {error && <p className="gx-error" role="alert">{error}</p>}
        <div className="gx-fila"><span className="gx-espacio" />
          <button type="button" className="gx-boton" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="gx-boton primario">Recordarme</button>
        </div>
      </form>
    </Modal>
  );
}

/** Un mensaje a tarea del planner (proyecto personal por defecto). */
export function ModalTarea({ chat, mensaje, onListo, onCerrar }) {
  const texto = mensaje?.texto ?? ``;
  const [titulo, setTitulo] = useState(texto ? texto.replace(/\s+/g, ` `).slice(0, 120) : `Responder a ${chat.nombre}`);
  const [proyectos, setProyectos] = useState(null);
  const [proyectoId, setProyectoId] = useState(``);
  const enDosDias = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().slice(0, 10); }, []);
  const [vence, setVence] = useState(enDosDias);
  const [error, setError] = useState(``);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    Promise.all([api(`/proyectos/personal`).catch(() => null), api(`/proyectos`).catch(() => [])]).then(([personal, lista]) => {
      const abiertos = (Array.isArray(lista) ? lista : []).filter((p) => p.estado !== `CERRADO` && !p.archivado && p.id !== personal?.id);
      setProyectos([...(personal ? [personal] : []), ...abiertos]);
      setProyectoId(String(personal?.id ?? abiertos[0]?.id ?? ``));
    });
  }, []);
  const crear = async (e) => {
    e.preventDefault();
    setGuardando(true); setError(``);
    const cuando = mensaje?.enviadoEn ? ` (${fechaHora(mensaje.enviadoEn)})` : ``;
    const descripcion = `Desde WhatsApp — ${chat.nombre}${cuando}${texto ? `:\n«${texto.slice(0, 2000)}»` : ``}`;
    try {
      await api(`/tareas`, { method: `POST`, body: { titulo: titulo.trim(), descripcion, proyectoId: Number(proyectoId), fechaVencimiento: vence }, clave: `gestor-tarea-${Date.now()}` });
      onListo();
    } catch (err) { setError(err.message); setGuardando(false); }
  };
  return (
    <Modal titulo="Convertir en tarea del planner" onCerrar={onCerrar}>
      <form className="gx-seccion" onSubmit={crear}>
        <label className="gx-campo">Tarea
          <input className="gx-entrada" autoFocus maxLength={250} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </label>
        <label className="gx-campo">Proyecto
          <select className="gx-select" value={proyectoId} onChange={(e) => setProyectoId(e.target.value)} disabled={!proyectos}>
            {!proyectos && <option>Cargando…</option>}
            {proyectos?.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </label>
        <label className="gx-campo">Vence
          <input type="date" className="gx-entrada" value={vence} onChange={(e) => setVence(e.target.value)} />
        </label>
        <small className="gx-texto">El mensaje y el chat quedan en la descripción de la tarea.</small>
        {error && <p className="gx-error" role="alert">{error}</p>}
        <div className="gx-fila"><span className="gx-espacio" />
          <button type="button" className="gx-boton" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="gx-boton primario" disabled={guardando || !titulo.trim() || !proyectoId}>Crear tarea</button>
        </div>
      </form>
    </Modal>
  );
}

/** El texto del resumen: «## Título» y «- punto» con un formato mínimo. */
function TextoResumen({ texto }) {
  return (
    <div className="gx-resumen-texto">
      {texto.split(`\n`).map((l, i) => {
        if (/^#{1,3}\s/.test(l)) return <h3 key={i}>{l.replace(/^#{1,3}\s/, ``)}</h3>;
        if (/^\s*[-*•]\s/.test(l)) return <p key={i} className="gx-viñeta">{l.replace(/^\s*[-*•]\s/, ``).replace(/\*\*/g, ``)}</p>;
        return l.trim() ? <p key={i}>{l.replace(/\*\*/g, ``)}</p> : null;
      })}
    </div>
  );
}

/** Resumen con IA: el último guardado y botón para pedir uno nuevo. */
export function ModalResumen({ api: gestor, onCerrar }) {
  const [resumen, setResumen] = useState(undefined);
  const [horas, setHoras] = useState(24);
  const [conGrupos, setConGrupos] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(``);
  useEffect(() => { gestor.resumen().then(setResumen).catch((e) => { setResumen(null); setError(e.message); }); }, [gestor]);
  const generar = async () => {
    setGenerando(true); setError(``);
    try { setResumen(await gestor.generarResumen({ horas, conGrupos })); } catch (e) { setError(e.message); } finally { setGenerando(false); }
  };
  return (
    <Modal titulo="Resumen con IA" onCerrar={onCerrar} ancho={640}>
      <div className="gx-fila">
        <select className="gx-select" aria-label="Período" value={horas} onChange={(e) => setHoras(Number(e.target.value))}>
          <option value={8}>Últimas 8 horas</option><option value={24}>Últimas 24 horas</option>
          <option value={48}>Últimas 48 horas</option><option value={72}>Últimos 3 días</option>
        </select>
        <label className="gx-opcion"><input type="checkbox" checked={conGrupos} onChange={(e) => setConGrupos(e.target.checked)} /> Incluir grupos</label>
        <span className="gx-espacio" />
        <button type="button" className="gx-boton primario" disabled={generando} onClick={generar}>{generando ? `Resumiendo…` : `Generar ahora`}</button>
      </div>
      <small className="gx-texto">Se envía sólo el texto de los mensajes a la API de Claude; nunca fotos ni archivos.</small>
      {error && <p className="gx-error" role="alert">{error}</p>}
      {resumen === undefined && <p className="gx-texto">Cargando…</p>}
      {resumen && !resumen.texto && !generando && <p className="gx-texto">Todavía no hay resúmenes. Genera el primero.</p>}
      {resumen?.texto && (
        <div className="gx-seccion" style={{ maxHeight: `60vh`, overflowY: `auto` }}>
          <small className="gx-texto">De {fechaHora(resumen.desde)} a {fechaHora(resumen.hasta)}</small>
          <TextoResumen texto={resumen.texto} />
        </div>
      )}
    </Modal>
  );
}

/** Reacciones bajo un mensaje: tocar la tuya la quita. */
export function Reacciones({ lista, onReaccionar }) {
  if (!lista?.length) return null;
  return (
    <div className="gx-reacciones">
      {lista.map((r) => (
        <button key={r.emoji} type="button" className={`gx-reaccion${r.mia ? ` mia` : ``}`}
          title={r.mia ? `Quitar tu reacción` : `Reaccionar igual`} onClick={() => onReaccionar?.(r.mia ? `` : r.emoji)}>
          {r.emoji}{r.cantidad > 1 && <span>{r.cantidad}</span>}
        </button>
      ))}
    </div>
  );
}

/** Encuestas, eventos, álbumes y menciones; null si el mensaje es de otro tipo. */
export function ContenidoEspecial({ m }) {
  const x = m.extra ?? {};
  if (m.tipo === `encuesta`) {
    const votos = new Map((m.votos ?? []).map((v) => [v.opcion, v]));
    const total = (m.votos ?? []).reduce((a, v) => a + v.cantidad, 0);
    return (
      <div className="gx-encuesta">
        <b>📊 {m.texto ?? `Encuesta`}</b>
        {(x.opciones ?? []).map((o) => {
          const v = votos.get(o);
          const pct = total ? Math.round(((v?.cantidad ?? 0) / total) * 100) : 0;
          return (
            <div key={o} className={`gx-opcion-encuesta${v?.mio ? ` mia` : ``}`}>
              <span style={{ width: `${pct}%` }} />
              <em>{o}</em><small>{v?.cantidad ?? 0}</small>
            </div>
          );
        })}
        <small className="gx-texto">{x.multiple ? `Varias respuestas` : `Una respuesta`} · {total} {total === 1 ? `voto` : `votos`}</small>
      </div>
    );
  }
  if (m.tipo === `evento`) {
    return (
      <div className="gx-encuesta">
        <b>📅 {m.texto ?? `Evento`}{x.cancelado ? ` · cancelado` : ``}</b>
        {x.inicio && <span>{fechaHora(new Date(x.inicio * 1000))}</span>}
        {x.ubicacion && <span>📍 {x.ubicacion}</span>}
        {x.descripcion && <small className="gx-texto">{x.descripcion}</small>}
      </div>
    );
  }
  if (m.tipo === `album`) {
    const partes = [x.fotos ? `${x.fotos} ${x.fotos === 1 ? `foto` : `fotos`}` : null, x.videos ? `${x.videos} ${x.videos === 1 ? `video` : `videos`}` : null].filter(Boolean);
    return <span className="gx-texto">🖼 Álbum · {partes.join(` y `) || `fotos`} (abajo)</span>;
  }
  if (m.tipo === `mencion`) return <span className="gx-texto">📣 {m.texto ?? `Te mencionó en su estado`}</span>;
  return null;
}

// ------------------------------------------------------------------ chats ocultos, hallazgos, notas, búsqueda, PDF (2026-10-07)

/** El segundo PIN: elegirlo (la primera vez) o escribirlo para ver los chats ocultos. */
export function ModalPinOcultos({ api: gestor, configurado, onAbierto, onCerrar }) {
  const [pin, setPin] = useState(``);
  const [repetido, setRepetido] = useState(``);
  const [error, setError] = useState(``);
  const [enviando, setEnviando] = useState(false);
  const valido = /^\d{6,12}$/.test(pin) && (configurado || pin === repetido);
  const enviar = async (e) => {
    e.preventDefault();
    if (!valido) return;
    setEnviando(true); setError(``);
    try {
      const r = configurado ? await gestor.abrirOcultos(pin) : await gestor.pinOcultos(pin);
      onAbierto(r.llave);
    } catch (err) { setError(err.message); setPin(``); setEnviando(false); }
  };
  const solo = (v) => v.replace(/\D/g, ``).slice(0, 12);
  return (
    <Modal titulo={configurado ? `Chats ocultos` : `Elige el PIN de los chats ocultos`} onCerrar={onCerrar} ancho={360}>
      <form className="gx-seccion" onSubmit={enviar}>
        <p className="gx-texto" style={{ margin: 0 }}>
          {configurado ? `Escribe el PIN de los chats ocultos.` : `De 6 a 12 números, distinto al del gestor. Sin él, esos chats no aparecen en ninguna parte.`}
        </p>
        <input className="gx-entrada" type="password" inputMode="numeric" autoFocus aria-label="PIN de ocultos" value={pin}
          style={{ textAlign: `center`, letterSpacing: `.4em` }} onChange={(e) => setPin(solo(e.target.value))} />
        {!configurado && (
          <input className="gx-entrada" type="password" inputMode="numeric" aria-label="Repite el PIN" placeholder="Repítelo" value={repetido}
            style={{ textAlign: `center`, letterSpacing: `.4em` }} onChange={(e) => setRepetido(solo(e.target.value))} />
        )}
        {error && <p className="gx-error" role="alert">{error}</p>}
        <div className="gx-fila"><span className="gx-espacio" />
          <button type="button" className="gx-boton" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="gx-boton primario" disabled={!valido || enviando}>{configurado ? `Abrir` : `Guardar y abrir`}</button>
        </div>
      </form>
    </Modal>
  );
}

const NOMBRE_TIPO = { imagen: `📷 Foto`, video: `🎬 Video`, audio: `🎤 Audio`, documento: `📄 Documento`, ubicacion: `📍 Ubicación`, contacto: `👤 Contacto`, sticker: `Sticker`, encuesta: `📊 Encuesta`, evento: `📅 Evento` };

/** Una lista de mensajes encontrados (búsqueda, eliminados, «ver una vez»). Tocar uno abre su chat. */
export function ListaHallazgos({ lista, vacio, onAbrir, onAnotar, modo = `buscar` }) {
  if (!lista) return <div className="gx-vacio">Buscando…</div>;
  if (!lista.length) return <div className="gx-vacio">{vacio}</div>;
  return (
    <div className="gx-hallazgos">
      {lista.map((h) => (
        <div key={h.mensajeId} className={`gx-hallazgo${modo === `unavez` && !h.abierto ? ` pendiente` : ``}`} role="button" tabIndex={0}
          onClick={() => onAbrir(h)} onKeyDown={(e) => { if (e.key === `Enter`) onAbrir(h); }}>
          <div className="gx-fila" style={{ gap: 6 }}>
            <b>{h.chat}</b>
            <span className="gx-espacio" />
            <small className="gx-texto">{fechaHora(modo === `eliminados` && h.eliminadoEn ? h.eliminadoEn : h.enviadoEn)}</small>
          </div>
          <p>
            {h.deMi ? `Tú: ` : h.autor ? `${h.autor}: ` : ``}
            {h.tipo !== `texto` && <em>{NOMBRE_TIPO[h.tipo] ?? h.tipo} </em>}
            {h.texto ?? ``}
          </p>
          {modo === `eliminados` && <small className="gx-error">Borrado {h.deMi ? `por ti` : `por ${h.autor ?? h.chat}`} · enviado {fechaHora(h.enviadoEn)}</small>}
          {modo === `unavez` && (
            <div className="gx-fila" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
              <label className="gx-opcion"><input type="checkbox" checked={h.abierto} onChange={() => onAnotar(h, { abierto: !h.abierto })} /> Ya la abrí en el celular</label>
              {h.nota && <small className="gx-texto">📝 {h.nota}</small>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Nota propia sobre un mensaje (se guarda cifrada). */
export function EditorNota({ m, api: gestor, onListo }) {
  const [nota, setNota] = useState(m.nota ?? ``);
  const [error, setError] = useState(``);
  const guardar = async (e) => {
    e.preventDefault();
    try { await gestor.anotar(m.id, { nota: nota.trim() }); onListo(); } catch (err) { setError(err.message); }
  };
  return (
    <form className="gx-nota-editar" onSubmit={guardar} onClick={(e) => e.stopPropagation()}>
      <input className="gx-entrada" autoFocus maxLength={1000} placeholder={m.verUnaVez ? `¿Qué decía? (para tener constancia)` : `Nota sobre este mensaje`}
        value={nota} onChange={(e) => setNota(e.target.value)} onKeyDown={(e) => { if (e.key === `Escape`) { e.stopPropagation(); onListo(); } }} />
      <button type="submit" className="gx-boton chico">Guardar</button>
      {error && <span className="gx-error">{error}</span>}
    </form>
  );
}

const TIPOS_BUSQUEDA = [[``, `Todo`], [`imagen`, `Fotos`], [`video`, `Videos`], [`documento`, `Documentos`], [`audio`, `Audios`], [`enlace`, `Enlaces`], [`ubicacion`, `Ubicaciones`]];

/** Buscar dentro de una conversación: texto, tipo y fechas. */
export function BuscadorChat({ chat, api: gestor, onCerrar }) {
  const [q, setQ] = useState(``);
  const [tipo, setTipo] = useState(``);
  const [desde, setDesde] = useState(``);
  const [hasta, setHasta] = useState(``);
  const [lista, setLista] = useState(undefined);
  const [error, setError] = useState(``);
  useEffect(() => {
    if (q.trim().length < 2 && !tipo) { setLista(undefined); return undefined; }
    const t = setTimeout(() => {
      setLista(null); setError(``);
      gestor.buscar({ q: q.trim(), tipo, chatId: chat.id, desde, hasta }).then(setLista).catch((e) => { setError(e.message); setLista([]); });
    }, 350);
    return () => clearTimeout(t);
  }, [q, tipo, desde, hasta, chat.id, gestor]);
  return (
    <div className="gx-buscar-chat" onKeyDown={(e) => { if (e.key === `Escape`) { e.stopPropagation(); onCerrar(); } }}>
      <div className="gx-fila">
        <input className="gx-entrada" style={{ flex: 1, minWidth: 160 }} autoFocus placeholder={`Buscar en ${chat.nombre}`} aria-label="Buscar en el chat"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <input type="date" className="gx-entrada corta" style={{ width: 140 }} aria-label="Desde" value={desde} onChange={(e) => setDesde(e.target.value)} />
        <input type="date" className="gx-entrada corta" style={{ width: 140 }} aria-label="Hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        <button type="button" className="gx-boton chico" onClick={onCerrar}>Cerrar</button>
      </div>
      <div className="gx-fila" style={{ gap: 4 }}>
        {TIPOS_BUSQUEDA.map(([v, t]) => (
          <button key={v} type="button" className={`gx-chip sin-punto${tipo === v ? ` activo gx-c-azul` : ``}`} onClick={() => setTipo(v)}>{t}</button>
        ))}
      </div>
      {error && <p className="gx-error">{error}</p>}
      {lista !== undefined && (
        <div className="gx-buscar-resultados">
          <ListaHallazgos lista={lista} vacio="Nada con ese filtro." onAbrir={() => {}} />
          {lista?.length === 100 && <small className="gx-texto">Se muestran los 100 más recientes: afina la búsqueda.</small>}
        </div>
      )}
    </div>
  );
}

/**
 * Exportar un chat: arma una página imprimible (con fotos) en otra pestaña y
 * abre «Imprimir» para guardarla como PDF. Trae hasta 5000 mensajes.
 */
export async function exportarChat({ chat, api: gestor, cliente, alAvanzar = () => {} }) {
  const ventana = window.open(``, `_blank`);
  if (!ventana) throw new Error(`El navegador bloqueó la ventana nueva: permite ventanas emergentes para exportar.`);
  ventana.document.write(`<p style="font-family:sans-serif">Preparando el PDF…</p>`);
  let todos = [];
  let antes = null;
  for (let i = 0; i < 25; i += 1) {
    const pagina = await gestor.mensajes(chat.id, antes, 200);
    todos = [...pagina, ...todos];
    alAvanzar(todos.length);
    if (pagina.length < 200) break;
    antes = pagina[0].enviadoEn;
  }
  const esc = (t) => String(t ?? ``).replace(/[&<>"]/g, (c) => ({ "&": `&amp;`, "<": `&lt;`, ">": `&gt;`, '"': `&quot;` }[c]));
  let fotos = 0;
  const filas = [];
  let dia = ``;
  for (const m of todos) {
    const d = new Date(m.enviadoEn).toLocaleDateString(`es-PE`, { weekday: `long`, day: `numeric`, month: `long`, year: `numeric` });
    if (d !== dia) { filas.push(`<h2>${esc(d)}</h2>`); dia = d; }
    const quien = m.deMi ? `Tú` : chat.esGrupo ? (m.autor ?? `Alguien`) : chat.nombre;
    let cuerpo = esc(m.texto ?? ``).replace(/\n/g, `<br>`);
    if (m.tipo === `imagen` && m.tieneArchivo && fotos < 150) {
      try { cuerpo = `<img src="${await cliente.archivo(m.id)}">${cuerpo ? `<br>${cuerpo}` : ``}`; fotos += 1; } catch { cuerpo = `[foto] ${cuerpo}`; }
    } else if (m.tipo !== `texto`) {
      cuerpo = `<i>[${esc(m.tipo)}${m.nombreArchivo ? `: ${esc(m.nombreArchivo)}` : ``}]</i> ${cuerpo}`;
    }
    const marcas = [m.eliminadoEn ? `eliminado` : ``, m.editadoEn ? `editado` : ``, m.nota ? `nota: ${esc(m.nota)}` : ``].filter(Boolean).join(` · `);
    filas.push(`<div class="m${m.deMi ? ` yo` : ``}"><b>${esc(quien)}</b> <time>${new Date(m.enviadoEn).toLocaleTimeString(`es-PE`, { hour: `2-digit`, minute: `2-digit`, hour12: false })}</time><div>${cuerpo}</div>${marcas ? `<small>${marcas}</small>` : ``}</div>`);
  }
  ventana.document.open();
  ventana.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(chat.nombre)} — WhatsApp</title><style>
    body{font:13px/1.45 -apple-system,"Segoe UI",sans-serif;color:#1d242b;margin:24px auto;max-width:760px}
    h1{font-size:20px;margin:0} header p{color:#5e6b78;margin:4px 0 16px} h2{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#5e6b78;border-bottom:1px solid #d4dae0;padding-bottom:4px;margin:18px 0 8px}
    .m{padding:4px 0 4px 10px;border-left:2px solid transparent;break-inside:avoid} .m.yo{border-left-color:#558ba5} .m b{font-size:12px} time{color:#8a95a3;font-size:11px}
    .m img{max-width:280px;max-height:280px;border-radius:6px;margin-top:4px} small{color:#c2453a;font-size:11px}
    @media print{body{margin:0}}</style></head><body>
    <header><h1>${esc(chat.nombre)}</h1><p>${esc(chat.numero ?? (chat.esGrupo ? `Grupo` : ``))} · ${todos.length} mensajes · exportado el ${esc(new Date().toLocaleString(`es-PE`))}</p></header>
    ${filas.join(`\n`)}</body></html>`);
  ventana.document.close();
  let impreso = false;
  const imprimir = () => { if (!impreso) { impreso = true; ventana.focus(); ventana.print(); } };
  ventana.addEventListener(`load`, imprimir);
  setTimeout(imprimir, 1200); // por si la página escrita no dispara load
  return todos.length;
}
